// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

interface LeaderboardStructure {
  id: string;
  name: string;
  sections: Array<{
    id: string;
    name: string;
    display_order: number;
    fields: Array<{
      id: string;
      field_name: string;
      field_type: string;
      unit: string;
      display_order: number;
    }>;
  }>;
}

interface Entry {
  id?: string;
  values: Record<string, string>;
}

const LeaderboardEntries: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [leaderboard, setLeaderboard] = useState<LeaderboardStructure | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchLeaderboardData();
    }
  }, [id]);

  const fetchLeaderboardData = async () => {
    try {
      setIsLoading(true);

      // Fetch leaderboard structure
      const { data: leaderboardData, error: leaderboardError } = await supabase
        .from('leaderboards')
        .select(`
          id,
          name,
          leaderboard_sections(
            id,
            name,
            display_order,
            leaderboard_fields!section_id(
              id,
              field_name,
              field_type,
              unit,
              display_order
            )
          )
        `)
        .eq('id', id)
        .single();

      if (leaderboardError) throw leaderboardError;

      const structuredData: LeaderboardStructure = {
        id: leaderboardData.id,
        name: leaderboardData.name,
        sections: leaderboardData.leaderboard_sections
          .sort((a: any, b: any) => a.display_order - b.display_order)
          .map((section: any) => ({
            id: section.id,
            name: section.name,
            display_order: section.display_order,
            fields: section.leaderboard_fields
              .sort((a: any, b: any) => a.display_order - b.display_order)
          }))
      };

      setLeaderboard(structuredData);

      // Fetch existing entries
      const { data: entriesData, error: entriesError } = await supabase
        .from('leaderboard_entries')
        .select(`
          id,
          leaderboard_values(
            field_id,
            value
          )
        `)
        .eq('leaderboard_id', id);

      if (entriesError) throw entriesError;

      const formattedEntries = entriesData.map((entry: any) => ({
        id: entry.id,
        values: entry.leaderboard_values.reduce((acc: Record<string, string>, value: any) => {
          acc[value.field_id] = value.value;
          return acc;
        }, {})
      }));

      setEntries(formattedEntries);
    } catch (error) {
      console.error('Error fetching leaderboard data:', error);
      toast.error('Failed to fetch leaderboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const addEntry = () => {
    const newEntry: Entry = {
      values: {}
    };
    setEntries([...entries, newEntry]);
  };

  const updateEntry = (index: number, field: string, value: string) => {
    const updatedEntries = [...entries];
    updatedEntries[index].values[field] = value;
    setEntries(updatedEntries);
  };

  const removeEntry = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const saveEntries = async () => {
    if (!leaderboard) return;

    try {
      setIsSaving(true);

      // Delete existing entries for this leaderboard
      await supabase
        .from('leaderboard_entries')
        .delete()
        .eq('leaderboard_id', leaderboard.id);

      // Insert new entries
      for (const entry of entries) {
        // Only create entry if there are some values
        const hasValues = Object.values(entry.values).some(value => value.trim() !== '');
        if (!hasValues) continue;

        const { data: entryData, error: entryError } = await supabase
          .from('leaderboard_entries')
          .insert([{
            leaderboard_id: leaderboard.id,
            athlete_name: 'Entry' // placeholder since we removed athlete_name field
          }])
          .select()
          .single();

        if (entryError) throw entryError;

        // Insert values for this entry
        const valuesToInsert = Object.entries(entry.values)
          .filter(([_, value]) => value.trim() !== '')
          .map(([fieldId, value]) => ({
            entry_id: entryData.id,
            field_id: fieldId,
            value: value
          }));

        if (valuesToInsert.length > 0) {
          const { error: valuesError } = await supabase
            .from('leaderboard_values')
            .insert(valuesToInsert);

          if (valuesError) throw valuesError;
        }
      }

      toast.success('Entries saved successfully');
      fetchLeaderboardData(); // Refresh data
    } catch (error) {
      console.error('Error saving entries:', error);
      toast.error('Failed to save entries');
    } finally {
      setIsSaving(false);
    }
  };

  const renderFieldInput = (fieldId: string, fieldType: string, value: string, entryIndex: number) => {
    const inputProps = {
      value: value || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => updateEntry(entryIndex, fieldId, e.target.value),
      className: "w-full"
    };

    switch (fieldType) {
      case 'number':
        return <Input {...inputProps} type="number" step="1" />;
      case 'decimal':
        return <Input {...inputProps} type="number" step="0.01" />;
      case 'time':
        return <Input {...inputProps} placeholder="MM:SS.mmm" />;
      case 'text':
        return <Input {...inputProps} />;
      default:
        return <Input {...inputProps} />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!leaderboard) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Leaderboard not found</h2>
        <Button onClick={() => navigate('/admin-dashboard/leaderboards')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Leaderboards
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin-dashboard/leaderboards')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Leaderboards
          </Button>
          <h1 className="text-3xl font-bold text-primary">{leaderboard.name} - Data Entry</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={addEntry}>
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
          <Button onClick={saveEntries} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {leaderboard.sections.map((section) => (
          <Card key={section.id}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-sm font-semibold">
                  {section.name}
                </span>
                <span className="text-lg">Division Data</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      {section.fields.map((field) => (
                        <th key={field.id} className="text-left p-2 font-semibold min-w-[120px]">
                          {field.field_name}
                          {field.unit && <span className="text-xs text-muted-foreground"> ({field.unit})</span>}
                        </th>
                      ))}
                      <th className="text-left p-2 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, entryIndex) => (
                      <tr key={entryIndex} className="border-b hover:bg-muted/50">
                        {section.fields.map((field) => (
                          <td key={field.id} className="p-2">
                            {renderFieldInput(
                              field.id,
                              field.field_type,
                              entry.values[field.id] || '',
                              entryIndex
                            )}
                          </td>
                        ))}
                        <td className="p-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => removeEntry(entryIndex)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {entries.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No entries added yet. Click "Add Entry" to get started.
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default LeaderboardEntries;